import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { GeometryType } from "@evergis/api";

import { Positions } from "ducks/geometryEditor/geometryEditor";
import { usePostgresLayer } from "../layer/usePostgresLayer";
import { NO_CONTENT_VALUE } from "../../utils/constants";
import { formatAttributeValue } from "../../utils/formatAttributeValue";
import { getLayerDefinition } from "../../evergis/core/layer/getLayerDefinition";
import { useFeatureLayerDefinition } from "./useFeatureLayerDefinition";
import { FeatureDcExtended } from "../../ducks/selectFeatures/types";
import { EditGeometryType } from "../../evergis/core/attributes/geometryTypes";
import { LayerInfo } from "../../interfaces/resourceInfo";

type FeatureData = {
  geometryType?: GeometryType | EditGeometryType;
  postgresLayer?: LayerInfo;
  coordinates?: Positions;
  featureName?: string;
  featureLayer?: string;
  featureTable?: string;
  loading?: boolean;
};

export const useFeature = (
  feature?: FeatureDcExtended,
  defaultFeatureName: string = NO_CONTENT_VALUE,
  layerName?: string,
): FeatureData => {
  const { t } = useTranslation("featureCard");
  const { postgresLayer, loading } = usePostgresLayer(feature?.layer || layerName, { waitInput: true });
  const featureDefinition = useFeatureLayerDefinition(feature);

  return useMemo(() => {
    const layerDefinition = layerName ? getLayerDefinition(postgresLayer) : featureDefinition;
    const featureLayer = postgresLayer?.name || "";
    const geometryType =
      feature?.geometry?.type || postgresLayer?.geometryType || layerDefinition?.geometryType || GeometryType.Unknown;
    const titleAttribute = layerDefinition?.titleAttribute || layerDefinition?.titleAttribute;
    const idAttribute = layerDefinition?.idAttribute || layerDefinition?.idAttribute;

    if (!feature)
      return {
        postgresLayer,
        geometryType,
        featureLayer,
        loading,
      };

    const { coordinates } = feature.geometry || {};

    const featureName = feature
      ? !titleAttribute || titleAttribute === idAttribute || !feature.attributes
        ? feature.id
        : feature.attributes[titleAttribute]
        ? formatAttributeValue(
            layerDefinition?.attributes[titleAttribute].type,
            feature.attributes[titleAttribute],
            layerDefinition?.attributes[titleAttribute].stringFormat,
          )
        : defaultFeatureName
      : t("newObject");

    return {
      geometryType,
      coordinates,
      featureName,
      postgresLayer,
      featureLayer: featureLayer || layerName || feature.layer,
      featureTable: postgresLayer.tableName || "",
      loading,
    };
  }, [layerName, postgresLayer, featureDefinition, feature, loading, defaultFeatureName, t]);
};
